import type {
    AnchorHTMLAttributes,
    FC,
    MouseEvent,
    ReactNode
} from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { prefersReducedMotion } from '@/lib/gsap'
import { usePageTransition } from '@/components/generals/page-transition'

interface TransitionLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string
    children: ReactNode
    prefetch?: boolean
}

export const scrollToHash = (hash: string): boolean => {
    const target = document.getElementById(decodeURIComponent(hash))

    if (!target) return false

    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' })

    return true
}

const isPlainLeftClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey

export const TransitionLink: FC<TransitionLinkProps> = ({
    href,
    onClick,
    children,
    prefetch,
    ...rest
}): ReactNode => {
    const router = useRouter()
    const { navigate } = usePageTransition()

    const opensElsewhere = Boolean(rest.target && rest.target !== '_self')

    if (href.startsWith('#')) {
        const handleHashClick = (event: MouseEvent<HTMLAnchorElement>) => {
            onClick?.(event)

            if (event.defaultPrevented || !isPlainLeftClick(event)) return

            if (scrollToHash(href.slice(1))) {
                event.preventDefault()
                window.history.replaceState(null, '', href)
            }
        }

        return (
            <a href={href} onClick={handleHashClick} {...rest}>
                {children}
            </a>
        )
    }

    if (!href.startsWith('/')) {
        return (
            <a href={href} onClick={onClick} {...rest}>
                {children}
            </a>
        )
    }

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event)

        if (event.defaultPrevented || !isPlainLeftClick(event) || opensElsewhere) return

        const [pathAndQuery, hash] = href.split('#')
        const current = router.asPath.split('#')[0]
        const target = pathAndQuery || current

        if (target === current) {
            if (hash && scrollToHash(hash)) {
                event.preventDefault()
                window.history.replaceState(null, '', `#${hash}`)
            }

            return
        }

        event.preventDefault()
        navigate(href)
    }

    return (
        <Link href={href} onClick={handleClick} prefetch={prefetch} {...rest}>
            {children}
        </Link>
    )
}
